import { Moon, Sun, Menu } from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import { useAuthToken } from "@/guards/useAuthToken";
import { logoutApi } from "@/pages/auth/api/auth.api";
import { getInitials, getGreeting } from "@/utils";
import { Avatar } from "@/components/ui/avatar";

export function Header({ onMenuClick }: { onMenuClick?: () => void }) {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const { setToken } = useAuthToken();

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (refreshToken) {
      try {
        await logoutApi(refreshToken);
      } catch (error) {
        console.error("Logout API error:", error);
      }
    }
    setToken(null);
    navigate("/login");
  };

  const displayName =
    (user?.first_name
      ? `${user.first_name} ${user.last_name ?? ""}`.trim()
      : "") || "";

  const firstName = user?.first_name || displayName.split(" ")[0] || "";
  const displayRole = user?.job_role || "";

  return (
    <header className="h-16 border-b border-border bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl sticky top-0 z-30 flex items-center justify-between px-4 lg:px-8">
      <div className="flex items-center gap-4 flex-1">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden text-muted-foreground"
          onClick={onMenuClick}
        >
          <Menu className="h-5 w-5" />
        </Button>
        <span className="hidden lg:inline text-lg font-bold text-slate-800 dark:text-slate-100">
          {getGreeting()}
          {firstName ? `, ${firstName}` : ""} 👋
        </span>
      </div>

      <div className="flex items-center gap-3">
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
          onClick={() => setTheme(theme === "light" ? "dark" : "light")}
        >
          <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <span className="sr-only">Toggle theme</span>
        </Button>

        <div className="h-6 w-px bg-border mx-1" />

        {/* Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-3 hover:bg-slate-100 dark:hover:bg-slate-800 p-1 pr-3 rounded-full transition-colors outline-none focus-visible:ring-2 ring-purple-500 ring-offset-background ring-offset-1 cursor-pointer">
            <Avatar
              src={user?.profile_picture}
              name={displayName || "User"}
              className="h-8 w-8 rounded-full border border-slate-300 dark:border-slate-700"
              imageClassName="bg-slate-200 dark:bg-slate-800"
              fallbackClassName="border-purple-200 dark:border-purple-800 text-xs"
            />
            <div className="hidden md:flex flex-col items-start">
              <span className="text-sm font-semibold leading-none text-foreground">
                {displayName}
              </span>
              <span className="text-xs text-muted-foreground mt-1">
                {displayRole}
              </span>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 p-2">
            <DropdownMenuItem
              onClick={() => navigate("/profile")}
              className="hover:text-slate-800 hover:bg-slate-50 cursor-pointer p-2"
            >
              My Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-rose-500 hover:text-slate-800 hover:bg-slate-50 cursor-pointer p-2"
            >
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
